import { useState, useEffect } from 'react';
import { useAuth } from '../../../AuthContext';
import { fetchData } from '../../../apiService';
import LoadingOverlay from '../../common/LoadingOverlay';
import { useSessionValidation } from '../../../utils/sessionValidation';
import ExcelJS from 'exceljs';
import {
  Button,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Box,
  Typography
} from '@mui/material';
import { Search, FileDownload } from '@mui/icons-material';
import './LedgerReport.css';

// Import modals
import SaleModal from '../Sale/SaleModal';
import PurchaseModal from '../Purchase/PurchaseModal';
import SaleReturnModal from '../SaleReturn/SaleReturnModal';
import PurchaseReturnModal from '../PurchaseReturn/PurchaseReturnModal';
import JournalEntryModal from '../JournalEntry/JournalEntryModal';
import PaymentReceiptsModal from '../PaymentReceipts/PaymentReceiptsModal';

const LedgerReport = () => {
  // Session validation
  useSessionValidation();

  const { companyId, branchId, token, companyName, username } = useAuth();

  // Data states
  const [masters, setMasters] = useState([]);
  const [ledgerEntries, setLedgerEntries] = useState([]);
  const [selectedMaster, setSelectedMaster] = useState('');
  const [selectedMasterName, setSelectedMasterName] = useState('');

  // Modal states
  const [openSaleModal, setOpenSaleModal] = useState(false);
  const [openPurchaseModal, setOpenPurchaseModal] = useState(false);
  const [openSaleReturnModal, setOpenSaleReturnModal] = useState(false);
  const [openPurchaseReturnModal, setOpenPurchaseReturnModal] = useState(false);
  const [openJournalModal, setOpenJournalModal] = useState(false);
  const [openPaymentModal, setOpenPaymentModal] = useState(false);
  const [selectedRecordId, setSelectedRecordId] = useState(null);

  // Modal data states
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [items, setItems] = useState([]);
  const [banks, setBanks] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [sales, setSales] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [vatRates, setVatRates] = useState([]);
  const [gstRates, setGstRates] = useState([]);

  // Sale modal data
  const [saleFormData, setSaleFormData] = useState({});
  const [saleDetails, setSaleDetails] = useState([]);
  const [relatedItems, setRelatedItems] = useState([]);
  const [freightPackingGST, setFreightPackingGST] = useState({ freight: {}, packing: {} });

  // Purchase modal data
  const [purchaseFormData, setPurchaseFormData] = useState({});
  const [purchaseDetails, setPurchaseDetails] = useState([]);

  // Sale Return modal data
  const [saleReturnData, setSaleReturnData] = useState(null);
  const [saleReturnDetails, setSaleReturnDetails] = useState([]);

  // Purchase Return modal data
  const [purchaseReturnData, setPurchaseReturnData] = useState(null);

  // Journal modal data
  const [journalData, setJournalData] = useState(null);
  const [journalDetails, setJournalDetails] = useState([]);

  // Payment modal data
  const [paymentData, setPaymentData] = useState(null);
  const [paymentDetails, setPaymentDetails] = useState([]);

  // Loading states
  const [modalLoading, setModalLoading] = useState(false);

  // Date states
  const formatDateToYYYYMMDD = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getDefaultFromDate = () => {
    const today = new Date();
    return formatDateToYYYYMMDD(new Date(today.getFullYear(), today.getMonth(), 1));
  };

  const getCurrentDate = () => formatDateToYYYYMMDD(new Date());

  const [fromDate, setFromDate] = useState(getDefaultFromDate());
  const [toDate, setToDate] = useState(getCurrentDate());

  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Loading...');

  // Search state
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (companyId && token) {
      fetchMasters();
    }
  }, [companyId, token]);

  const fetchMasters = async () => {
    setLoading(true);
    setLoadingMessage('Fetching masters...');
    try {
      // Fetch all masters (same as JournalEntry)
      const response = await fetchData('JournalEntryApi', `/Master/${companyId}`, token);
      const allMasters = (response.response_data || response || []).sort((a, b) =>
        (a.Name || '').localeCompare(b.Name || '')
      );

      setMasters(allMasters);
    } catch (error) {
      console.error('Error fetching masters:', error);
      setMasters([]);
    }
    setLoading(false);
  };

  const fetchLedgerData = async () => {
    if (!selectedMaster || !fromDate || !toDate) {
      return;
    }

    setLoading(true);
    setLoadingMessage('Fetching ledger data...');
    try {
      // API endpoint for ledger report
      const response = await fetchData(
        'ReportApi',
        `/LedgerDetails/${companyId}/${selectedMaster}/${fromDate}/${toDate}`,
        token
      );

      const data = response.response_data || response || [];
      setLedgerEntries(data);
    } catch (error) {
      console.error('Error fetching ledger data:', error);
      setLedgerEntries([]);
    }
    setLoading(false);
  };

  const handleMasterChange = (event) => {
    const masterId = event.target.value;
    setSelectedMaster(masterId);

    const master = masters.find(m => m.Id === masterId);
    setSelectedMasterName(master ? master.Name : '');

    // Clear previous data
    setLedgerEntries([]);
  };

  const handleGetLedger = () => {
    fetchLedgerData();
  };

  // Filter masters based on search
  const filteredMasters = masters.filter(master =>
    master.Name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Format currency - only show AED for closing balance
  const formatCurrency = (amount, showCurrency = true) => {
    const value = parseFloat(amount) || 0;
    if (showCurrency) {
      return `AED ${value.toFixed(2)}`;
    }
    return value.toFixed(2);
  };

  // Format currency - Not show AED for closing balance
  const formatCurrencyNotAED = (amount, showCurrency = true) => {
    const value = parseFloat(amount) || 0;
    if (showCurrency) {
      return ` ${value.toFixed(2)}`;
    }
    return value.toFixed(2);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB');
  };

  // Export to Excel
  const exportToExcel = async () => {
    if (ledgerEntries.length === 0) return;

    // Create a new workbook
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Ledger Report');

    // Set column widths
    worksheet.columns = [
      { width: 10 },  // Sl No
      { width: 15 },  // Date
      { width: 40 },  // Description
      { width: 18 },  // Debit
      { width: 18 },  // Credit
      { width: 22 }   // Balance
    ];

    // Add Company Header
    const companyRow = worksheet.addRow([companyName || 'Company Name']);
    worksheet.mergeCells(`A1:F1`);
    companyRow.height = 28;
    companyRow.getCell(1).font = { bold: true, size: 16, color: { argb: 'FF1F4788' } };
    companyRow.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };
    companyRow.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD6EAF8' } };
    companyRow.getCell(1).border = {
      top: { style: 'medium', color: { argb: 'FF1F4788' } },
      bottom: { style: 'medium', color: { argb: 'FF1F4788' } },
      left: { style: 'medium', color: { argb: 'FF1F4788' } },
      right: { style: 'medium', color: { argb: 'FF1F4788' } }
    };

    // Add Title
    const titleRow = worksheet.addRow([`Ledger Report - ${selectedMasterName}`]);
    worksheet.mergeCells(`A2:F2`);
    titleRow.height = 24;
    titleRow.getCell(1).font = { bold: true, size: 13, color: { argb: 'FFFFFFFF' } };
    titleRow.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };
    titleRow.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF7B3F8F' } };

    // Add Period
    const periodRow = worksheet.addRow([`Period: ${formatDate(fromDate)} to ${formatDate(toDate)}`]);
    worksheet.mergeCells(`A3:F3`);
    periodRow.height = 20;
    periodRow.getCell(1).font = { size: 10, color: { argb: 'FF2C3E50' } };
    periodRow.getCell(1).alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
    periodRow.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8F9FA' } };

    // Add Generated Time
    const generatedRow = worksheet.addRow([`Generated: ${new Date().toLocaleString('en-GB')}`]);
    worksheet.mergeCells(`A4:F4`);
    generatedRow.height = 20;
    generatedRow.getCell(1).font = { size: 10, color: { argb: 'FF2C3E50' } };
    generatedRow.getCell(1).alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
    generatedRow.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8F9FA' } };

    // Add Generated By
    const generatedByRow = worksheet.addRow([`Generated By: ${username || 'User'}`]);
    worksheet.mergeCells(`A5:F5`);
    generatedByRow.height = 20;
    generatedByRow.getCell(1).font = { size: 10, color: { argb: 'FF2C3E50' } };
    generatedByRow.getCell(1).alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
    generatedByRow.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8F9FA' } };

    // Empty row
    worksheet.addRow([]);

    // Add Table Headers
    const headerRow = worksheet.addRow(['Sl No', 'Date', 'Description', 'Debit', 'Credit', 'Balance']);
    headerRow.height = 24;

    // Apply styling only to cells A-F (columns 1-6)
    for (let col = 1; col <= 6; col++) {
      const cell = headerRow.getCell(col);
      cell.font = { bold: true, size: 11, color: { argb: 'FFFFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF7B3F8F' } };
      cell.border = {
        top: { style: 'medium', color: { argb: 'FF000000' } },
        bottom: { style: 'medium', color: { argb: 'FF000000' } },
        left: { style: 'thin', color: { argb: 'FFFFFFFF' } },
        right: { style: 'thin', color: { argb: 'FFFFFFFF' } }
      };
      // Right align Debit, Credit, Balance headers (columns 4, 5, 6)
      if (col >= 4) {
        cell.alignment = { horizontal: 'right', vertical: 'middle', indent: 1 };
      } else {
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
      }
    }

    // Add Data Rows
    let runningBalance = 0;
    ledgerEntries.forEach((entry, index) => {
      const isTotal = entry.description1 === 'Total';
      const isClosing = entry.description1 === 'Closing Balance';
      const isOpening = entry.description1 === 'Opening Balance';

      // Calculate running balance
      if (isOpening) {
        runningBalance = parseFloat(entry.Balance) || 0;
      } else if (!isTotal && !isClosing) {
        const debit = parseFloat(entry.debit) || 0;
        const credit = parseFloat(entry.credit) || 0;
        runningBalance = runningBalance + debit - credit;
      } else if (isClosing) {
        runningBalance = parseFloat(entry.Balance) || runningBalance;
      }

      const displayBalance = isTotal ? '' : runningBalance;

      const dataRow = worksheet.addRow([
        isTotal || isClosing ? '' : index + 1,
        formatDate(entry.date1),
        entry.description1 || '-',
        entry.debit && parseFloat(entry.debit) !== 0
          ? (isClosing ? `AED ${parseFloat(entry.debit).toFixed(2)}` : parseFloat(entry.debit).toFixed(2))
          : '-',
        entry.credit && parseFloat(entry.credit) !== 0
          ? (isClosing ? `AED ${parseFloat(entry.credit).toFixed(2)}` : parseFloat(entry.credit).toFixed(2))
          : '-',
        isTotal ? '-' : (isClosing
          ? `AED ${Math.abs(displayBalance).toFixed(2)} ${displayBalance < 0 ? '(Cr)' : '(Dr)'}`
          : Math.abs(displayBalance).toFixed(2))
      ]);

      // Apply styling based on row type - only to columns 1-6
      if (isClosing) {
        // Closing Balance row
        for (let col = 1; col <= 6; col++) {
          const cell = dataRow.getCell(col);
          cell.font = { bold: true, size: 11, color: { argb: 'FF1F4788' } };
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD6EAF8' } };
          cell.border = {
            top: { style: 'medium', color: { argb: 'FF1F4788' } },
            bottom: { style: 'medium', color: { argb: 'FF1F4788' } },
            left: { style: 'thin', color: { argb: 'FF1F4788' } },
            right: { style: 'thin', color: { argb: 'FF1F4788' } }
          };
          if (col === 1 || col === 2) {
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
          } else if (col === 3) {
            cell.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
          } else {
            cell.alignment = { horizontal: 'right', vertical: 'middle', indent: 1 };
          }
        }
      } else if (isTotal) {
        // Total row
        for (let col = 1; col <= 6; col++) {
          const cell = dataRow.getCell(col);
          cell.font = { bold: true, size: 10 };
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8E8E8' } };
          cell.border = {
            top: { style: 'medium', color: { argb: 'FF000000' } },
            bottom: { style: 'thin', color: { argb: 'FF000000' } },
            left: { style: 'thin', color: { argb: 'FFD5D8DC' } },
            right: { style: 'thin', color: { argb: 'FFD5D8DC' } }
          };
          if (col === 1 || col === 2) {
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
          } else if (col === 3) {
            cell.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
          } else {
            cell.alignment = { horizontal: 'right', vertical: 'middle', indent: 1 };
          }
        }
      } else {
        // Regular rows
        const bgColor = index % 2 === 0 ? 'FFFFFFFF' : 'FFF8F9FA';
        for (let col = 1; col <= 6; col++) {
          const cell = dataRow.getCell(col);
          cell.font = { size: 10 };
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bgColor } };
          cell.border = {
            top: { style: 'thin', color: { argb: 'FFD5D8DC' } },
            bottom: { style: 'thin', color: { argb: 'FFD5D8DC' } },
            left: { style: 'thin', color: { argb: 'FFD5D8DC' } },
            right: { style: 'thin', color: { argb: 'FFD5D8DC' } }
          };
          if (col === 1 || col === 2) {
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
          } else if (col === 3) {
            cell.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
          } else {
            cell.alignment = { horizontal: 'right', vertical: 'middle', indent: 1 };
          }
        }
      }
    });

    // Generate filename
    const filename = `Ledger_${selectedMasterName}_${new Date().toISOString().split('T')[0]}.xlsx`;

    // Save file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  // Fetch data required for modals
  const fetchModalData = async () => {
    try {
      // Fetch customers
      const customersResponse = await fetchData('CustomerApi', `/${companyId}`, token);
      setCustomers(customersResponse.response_data || customersResponse || []);

      // Fetch suppliers
      const suppliersResponse = await fetchData('VendorApi', `/${companyId}`, token);
      setSuppliers(suppliersResponse.response_data || suppliersResponse || []);

      // Fetch items
      const itemsResponse = await fetchData('ItemApi', `/${companyId}`, token);
      setItems(itemsResponse.response_data || itemsResponse || []);

      // Fetch banks
      const banksResponse = await fetchData('BankApi', `/${companyId}`, token);
      setBanks(banksResponse.response_data || banksResponse || []);
    } catch (error) {
      console.error('Error fetching modal data:', error);
    }
  };

  // Fetch sale data by ID
  const fetchSaleById = async (saleId) => {
    try {
      setModalLoading(true);
      const response = await fetchData('SaleApi', `/${saleId}`, token);
      const freshData = response.response_data || response;

      if (freshData) {
        const saleData = freshData.Sale || freshData;
        const details = freshData.SaleDetails || [];

        // Format the data to match what SaleModal expects
        setSaleFormData({
          // Id: saleData.Id,
          // CompanyId: saleData.CompanyId || companyId,
          // BranchId: saleData.BranchId || branchId,
          // MasterId: saleData.MasterId,
          // CustomerName: saleData.CustomerName || '',
          // VATNumber: saleData.GSTNumber || saleData.VATNumber || '',
          // SalesManId: saleData.SalesManId || null,
          // StateId: saleData.StateId || null,
          // BillNumber: saleData.BillNumber,
          // BillPrefix: saleData.BillPrefix || '',
          // FinYear: saleData.FinYear || '',
          // BillDate: saleData.BillDate?.split('T')[0] || new Date().toISOString().split('T')[0],
          // ClearingDate: saleData.ClearingDate?.split('T')[0] || new Date().toISOString().split('T')[0],
          // GstCalculationType: saleData.GstCalculationType || 1,
          // GstCalculationTypeName: saleData.GstCalculationTypeName || 'Excluding Tax',
          // Amount: saleData.Amount || 0,
          // DiscountAmount: saleData.DiscountAmount || 0,
          // TaxableAmount: saleData.TaxableAmount || 0,
          // VATAmount: saleData.VATAmount || 0,
          // FreightAmount: saleData.FreightAmount || 0,
          // FreightVATRate: saleData.FreightVATRate || 0,
          // FreightVATAmount: saleData.FreightVATAmount || 0,
          // PackingAmount: saleData.PackingAmount || 0,
          // PackingVATRate: saleData.PackingVATRate || 0,
          // PackingVATAmount: saleData.PackingVATAmount || 0,
          // RoundOff: saleData.RoundOff || 0,
          // TotalAmount: saleData.TotalAmount || saleData.GrandTotal || 0,
          // GrandTotal: saleData.GrandTotal || saleData.TotalAmount || 0,
          // CashAmount: saleData.CashAmount || 0,
          // UPIAmount: saleData.UPIAmount || 0,
          // CardAmount: saleData.CardAmount || 0,
          // BankId: saleData.BankId || null,
          // PaidAmount: saleData.PaidAmount || 0,
          // BalanceAmount: saleData.BalanceAmount || 0,
          // Remarks: saleData.Remarks || '',
          // DispatchFrom: saleData.DispatchFrom || '',
          // DispatchAddress1: saleData.DispatchAddress1 || '',
          // DispatchAddress2: saleData.DispatchAddress2 || '',
          // DispatchLocation: saleData.DispatchLocation || '',
          // DispatchPincode: saleData.DispatchPincode || '',
          // ShipTo: saleData.ShipTo || '',
          // ShipGSTNo: saleData.ShipGSTNo || '',
          // ShipToAddress1: saleData.ShipToAddress1 || '',
          // ShipToAddress2: saleData.ShipToAddress2 || '',
          // ShipToLocation: saleData.ShipToLocation || '',
          // ShipToPincode: saleData.ShipToPincode || '',
          // TransporterName: saleData.TransporterName || '',
          // TransporterVAT: saleData.TransporterVAT || '',
          // VehicleNumber: saleData.VehicleNumber || '',
          // Distance: saleData.Distance || '',
          // IsGST: saleData.IsGST ?? true,
          // IsActive: saleData.IsActive ?? true,
          // CreatedBy: saleData.CreatedBy,
          // CreatedDate: saleData.CreatedDate,
          // CreatedByName: saleData.CreatedByName,
          // ModifiedBy: saleData.ModifiedBy,
          // ModifiedDate: saleData.ModifiedDate,
          // ModifiedByName: saleData.ModifiedByName
          Id: saleData.Id,
          CompanyId: saleData.CompanyId || companyId,
          BranchId: saleData.BranchId || branchId,
          MasterId: saleData.MasterId,
          CustomerName: saleData.CustomerName || '',
          VATNumber: saleData.GSTNumber || saleData.VATNumber || '',
          SalesManId: saleData.SalesManId || null,
          StateId: saleData.StateId || null,
          BillNumber: saleData.BillNumber,
          BillPrefix: saleData.BillPrefix || '',
          FinYear: saleData.FinYear || '',
          BillDate: saleData.BillDate?.split('T')[0] || new Date().toISOString().split('T')[0],
          ClearingDate: saleData.ClearingDate?.split('T')[0] || new Date().toISOString().split('T')[0],
          GstCalculationType: saleData.GstCalculationType || 1,
          GstCalculationTypeName: saleData.GstCalculationTypeName || 'Excluding Tax',
          Amount: saleData.Amount || 0,
          DiscountAmount: saleData.DiscountAmount || 0,
          TaxableAmount: saleData.TaxableAmount || 0,
          VATAmount: saleData.VATAmount || 0,
          FreightAmount: saleData.FreightAmount || 0,
          FreightVATRate: saleData.FreightVATRate || 0,
          FreightVATAmount: saleData.FreightVATAmount || 0,
          PackingAmount: saleData.PackingAmount || 0,
          PackingVATRate: saleData.PackingVATRate || 0,
          PackingVATAmount: saleData.PackingVATAmount || 0,
          RoundOff: saleData.RoundOff || 0,
          TotalAmount: saleData.TotalAmount || saleData.GrandTotal || 0,
          GrandTotal: saleData.GrandTotal || saleData.TotalAmount || 0,
          CashAmount: saleData.CashAmount || 0,
          UPIAmount: saleData.UPIAmount || 0,
          CardAmount: saleData.CardAmount || 0,
          BankId: saleData.BankId || null,
          PaidAmount: saleData.PaidAmount || 0,
          BalanceAmount: saleData.BalanceAmount || 0,
          Remarks: saleData.Remarks || '',
          DispatchFrom: saleData.DispatchFrom || '',
          DispatchAddress1: saleData.DispatchAddress1 || '',
          DispatchAddress2: saleData.DispatchAddress2 || '',
          DispatchLocation: saleData.DispatchLocation || '',
          DispatchPincode: saleData.DispatchPincode || '',
          ShipTo: saleData.ShipTo || '',
          ShipGSTNo: saleData.ShipGSTNo || '',
          ShipToAddress1: saleData.ShipToAddress1 || '',
          ShipToAddress2: saleData.ShipToAddress2 || '',
          ShipToLocation: saleData.ShipToLocation || '',
          ShipToPincode: saleData.ShipToPincode || '',
          VehicleNumber: saleData.VehicleNumber || '',
          TransporterName: saleData.TransporterName || '',
          TransporterVAT: saleData.TransporterVAT || '',
          Distance: saleData.Distance || 0,
          IsVerified: saleData.IsVerified || false,
          IsActive: saleData.IsActive ?? true,
          CreatedBy: saleData.CreatedBy,
          CreatedDate: saleData.CreatedDate,
          CreatedByName: saleData.CreatedByName,
          ModifiedBy: saleData.ModifiedBy,
          ModifiedDate: saleData.ModifiedDate,
          ModifiedByName: saleData.ModifiedByName
        });
       
        setSaleDetails(details.map(d => {
          const quantity = d.Quantity || 0;
          const rate = d.Rate || 0;
          const calculatedAmount = quantity * rate;

          return {
            Id: d.Id || 0,
            ItemId: d.ItemId,
            ItemName: d.ItemName || '',
            HSNCode: d.HSNCode || '',
            VATRate: d.VATRate || d.GSTRate || 0,
            GSTRate: d.GSTRate || 0,
            Quantity: quantity,
            Rate: rate,
            Amount: d.Amount || calculatedAmount,
            DiscountPercentage: d.DiscountPercentage || 0,
            DiscountAmount: d.DiscountAmount || 0,
            TaxableAmount: d.TaxableAmount || 0,
            VATAmount: d.VATAmount || 0,
            TotalAmount: d.TotalAmount || 0,
            IsActive: d.IsActive ?? true,
            CreatedBy: d.CreatedBy,
            CreatedDate: d.CreatedDate,
            ModifiedBy: d.ModifiedBy,
            ModifiedDate: d.ModifiedDate
          };
        }));
      }
    } catch (error) {
      console.error('Error fetching sale data:', error);
    } finally {
      setModalLoading(false);
    }
  };

  // Fetch purchase data by ID
  const fetchPurchaseById = async (purchaseId) => {
    try {
      setModalLoading(true);
      const response = await fetchData('PurchaseApi', `/${purchaseId}`, token);
      const freshData = response.response_data || response;

      if (freshData) {
        const purchaseData = freshData.Purchase || freshData;
        const details = freshData.PurchaseDetails || [];

        setPurchaseFormData({
          Id: purchaseData.Id,
          CompanyId: purchaseData.CompanyId || companyId,
          BranchId: purchaseData.BranchId || branchId,
          MasterId: purchaseData.MasterId,
          MasterName: purchaseData.MasterName || '',
          VATNumber: purchaseData.GSTNumber || purchaseData.VATNumber || '',
          BillNumber: purchaseData.BillNumber,
          BillPrefix: purchaseData.BillPrefix || '',
          FinYear: purchaseData.FinYear || '',
          BillDate: purchaseData.BillDate?.split('T')[0] || new Date().toISOString().split('T')[0],
          ClearingDate: purchaseData.ClearingDate?.split('T')[0] || new Date().toISOString().split('T')[0],
          Amount: purchaseData.Amount || 0,
          DiscountAmount: purchaseData.DiscountAmount || 0,
          TaxableAmount: purchaseData.TaxableAmount || 0,
          VATAmount: purchaseData.VATAmount || 0,
          FreightAmount: purchaseData.FreightAmount || 0,
          FreightVATRate: purchaseData.FreightVATRate || 0,
          FreightVATAmount: purchaseData.FreightVATAmount || 0,
          PackingAmount: purchaseData.PackingAmount || 0,
          PackingVATRate: purchaseData.PackingVATRate || 0,
          PackingVATAmount: purchaseData.PackingVATAmount || 0,
          RoundOff: purchaseData.RoundOff || 0,
          TotalAmount: purchaseData.TotalAmount || 0,
          PaidAmount: purchaseData.PaidAmount || 0,
          BalanceAmount: purchaseData.BalanceAmount || 0,
          Remarks: purchaseData.Remarks || '',
          IsActive: purchaseData.IsActive ?? true
        });

        setPurchaseDetails(details.map(d => ({
          Id: d.Id || 0,
          ItemId: d.ItemId,
          ItemName: d.ItemName,
          HSNCode: d.HSNCode || '',
          VATRate: d.VATRate || d.GSTRate || 0,
          OrderQuantity: d.OrderQuantity || 0,
          ReceivedQuantity: d.ReceivedQuantity || 0,
          BilledQuantity: d.BilledQuantity || 0,
          DamageQuantity: d.DamageQuantity || 0,
          ExcessQuantity: d.ExcessQuantity || 0,
          LessQuantity: d.LessQuantity || 0,
          StockQuantity: d.StockQuantity || 0,
          Rate: d.Rate || 0,
          Amount: d.Amount || 0,
          DiscountPercentage: d.DiscountPercentage || 0,
          DiscountAmount: d.DiscountAmount || 0,
          TaxableAmount: d.TaxableAmount || 0,
          VATAmount: d.VATAmount || 0,
          TotalAmount: d.TotalAmount || 0,
          IsActive: d.IsActive ?? true
        })));
      }
    } catch (error) {
      console.error('Error fetching purchase data:', error);
    } finally {
      setModalLoading(false);
    }
  };

  // Fetch sale return data by ID
  const fetchSaleReturnById = async (saleReturnId) => {
    try {
      setModalLoading(true);
      const response = await fetchData('SaleReturnApi', `/${saleReturnId}`, token);
      const freshData = response.response_data || response;

      if (freshData) {
        setSaleReturnData(freshData.SaleReturn || freshData);
        setSaleReturnDetails(freshData.SaleReturnDetails || []);
      }
    } catch (error) {
      console.error('Error fetching sale return data:', error);
    } finally {
      setModalLoading(false);
    }
  };

  // Fetch purchase return data by ID
  const fetchPurchaseReturnById = async (purchaseReturnId) => {
    try {
      setModalLoading(true);
      const response = await fetchData('PurchaseReturnApi', `/${purchaseReturnId}`, token);
      const freshData = response.response_data || response;

      if (freshData) {
        setPurchaseReturnData(freshData);
      }
    } catch (error) {
      console.error('Error fetching purchase return data:', error);
    } finally {
      setModalLoading(false);
    }
  };

  // Fetch journal entry data by ID
  const fetchJournalById = async (journalId) => {
    try {
      setModalLoading(true);
      const response = await fetchData('JournalEntryApi', `/JournalEntry/${companyId}/${branchId}/${journalId}`, token);
      const freshData = response.response_data || response;

      if (freshData) {
        setJournalData(freshData.JournalEntry || freshData);
        setJournalDetails(freshData.JournalEntryDetails || []);
      }
    } catch (error) {
      console.error('Error fetching journal data:', error);
    } finally {
      setModalLoading(false);
    }
  };

  // Fetch payment/receipt data by ID
  const fetchPaymentById = async (paymentId) => {
    try {
      setModalLoading(true);
      const response = await fetchData('PaymentAndReceiptApi', `/${paymentId}`, token);
      const freshData = response.response_data || response;

      if (freshData) {
        setPaymentData(freshData.PaymentAndReceipt || freshData);
        setPaymentDetails(freshData.PaymentAndReceiptDetails || []);
      }
    } catch (error) {
      console.error('Error fetching payment data:', error);
    } finally {
      setModalLoading(false);
    }
  };

  const handleDescriptionClick = async (ledger) => {
    // Skip if it's Opening Balance, Total, or Closing Balance
    const description = (ledger.description1 || '').toLowerCase();
    if (description.includes('opening balance') || description.includes('total') || description.includes('closing balance')) {
      return;
    }

    const tableName = ledger.tbl1;
    const recordId = ledger.id;

    if (!tableName || !recordId) return;

    // Skip special table names
    if (['az', 'zy', 'zz'].includes(tableName.toLowerCase())) {
      return;
    }

    // Fetch modal data if not already loaded
    if (customers.length === 0 || items.length === 0 || banks.length === 0) {
      await fetchModalData();
    }

    // Open appropriate modal based on table name
    switch (tableName.toLowerCase()) {
      case 'sale':
        await fetchSaleById(recordId);
        setSelectedRecordId(recordId);
        setOpenSaleModal(true);
        break;
      case 'purchase':
        await fetchPurchaseById(recordId);
        setSelectedRecordId(recordId);
        setOpenPurchaseModal(true);
        break;
      case 'salereturn':
        await fetchSaleReturnById(recordId);
        setSelectedRecordId(recordId);
        setOpenSaleReturnModal(true);
        break;
      case 'purchasereturn':
        await fetchPurchaseReturnById(recordId);
        setSelectedRecordId(recordId);
        setOpenPurchaseReturnModal(true);
        break;
      case 'journal':
        await fetchJournalById(recordId);
        setSelectedRecordId(recordId);
        setOpenJournalModal(true);
        break;
      case 'paymentandreceipt':
        await fetchPaymentById(recordId);
        setSelectedRecordId(recordId);
        setOpenPaymentModal(true);
        break;
      default:
        console.log('No modal configured for table:', tableName);
        break;
    }
  };

  const handleCloseSaleModal = () => {
    setOpenSaleModal(false);
    setSelectedRecordId(null);
    setSaleFormData({});
    setSaleDetails([]);
  };

  const handleClosePurchaseModal = () => {
    setOpenPurchaseModal(false);
    setSelectedRecordId(null);
    setPurchaseFormData({});
    setPurchaseDetails([]);
  };

  const handleCloseSaleReturnModal = () => {
    setOpenSaleReturnModal(false);
    setSelectedRecordId(null);
    setSaleReturnData(null);
    setSaleReturnDetails([]);
  };

  const handleClosePurchaseReturnModal = () => {
    setOpenPurchaseReturnModal(false);
    setSelectedRecordId(null);
    setPurchaseReturnData(null);
  };

  const handleCloseJournalModal = () => {
    setOpenJournalModal(false);
    setSelectedRecordId(null);
    setJournalData(null);
    setJournalDetails([]);
  };

  const handleClosePaymentModal = () => {
    setOpenPaymentModal(false);
    setSelectedRecordId(null);
    setPaymentData(null);
    setPaymentDetails([]);
  };

  const handleSaveSale = () => {
    // This is view mode only, no save needed
    handleCloseSaleModal();
  };

  return (
    <div style={{
      minWidth: '100%',
      background: '#f5f7fa',
      padding: '10px',
      position: 'relative'
    }}>
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        padding: '12px',
        position: 'relative',
        color: 'black',
        fontFamily: 'Times New Roman',
      }}>
        {/* Header Section */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '10px',
          paddingBottom: '8px',
          borderBottom: '1px solid #f0f0f0'
        }}>
          <div>
            <h4 style={{
              fontSize: '17px',
              fontWeight: '700',
              margin: 0,
              letterSpacing: '-0.3px',
              fontFamily: 'Times New Roman',
              textTransform: 'uppercase'
            }}>
              <span style={{ color: 'black', fontFamily: 'Times New Roman' }}>Ledger Report</span>
            </h4>
          </div>
        </div>

        {/* Filter Section */}
        <div style={{
          display: 'flex',
          gap: '10px',
          alignItems: 'flex-end',
          marginBottom: '12px',
          flexWrap: 'wrap'
        }}>
          {/* Master Dropdown */}
          <FormControl size="small" style={{ minWidth: '300px', flex: 1 }}>
            <InputLabel id="master-select-label" style={{ fontSize: '13px' }}>
              Select Master
            </InputLabel>
            <Select
              labelId="master-select-label"
              value={selectedMaster}
              onChange={handleMasterChange}
              label="Select Master"
              sx={{
                borderRadius: '8px',
                backgroundColor: '#f8f9fa',
                fontSize: '13px',
                height: '35px',
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#667eea',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#667eea',
                },
              }}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 300,
                  },
                },
              }}
            >
              <MenuItem value="">
                <em>Select a master</em>
              </MenuItem>
              <Box sx={{ px: 1, py: 0.5 }}>
                <TextField
                  size="small"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search style={{ fontSize: '18px' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    width: '100%',
                    '& .MuiOutlinedInput-root': {
                      fontSize: '12px',
                    },
                  }}
                />
              </Box>
              {filteredMasters.map((master) => (
                <MenuItem key={master.Id} value={master.Id} style={{ fontSize: '13px' }}>
                  {master.Name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* From Date */}
          <TextField
            label="From Date"
            type="date"
            size="small"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{
              minWidth: '150px',
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                backgroundColor: '#f8f9fa',
                fontSize: '13px',
                height: '35px',
                '&:hover fieldset': {
                  borderColor: '#667eea',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#667eea',
                },
              },
            }}
          />

          {/* To Date */}
          <TextField
            label="To Date"
            type="date"
            size="small"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{
              minWidth: '150px',
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                backgroundColor: '#f8f9fa',
                fontSize: '13px',
                height: '35px',
                '&:hover fieldset': {
                  borderColor: '#667eea',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#667eea',
                },
              },
            }}
          />

          {/* Get Ledger Button */}
          <Button
            variant="contained"
            onClick={handleGetLedger}
            disabled={!selectedMaster || !fromDate || !toDate}
            style={{
              background: selectedMaster && fromDate && toDate
                ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                : '#ccc',
              color: 'white',
              padding: '6px 20px',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: '700',
              boxShadow: selectedMaster && fromDate && toDate
                ? '0 2px 8px rgba(102, 126, 234, 0.4)'
                : 'none',
              border: 'none',
              height: '35px',
              textTransform: 'none',
              letterSpacing: '0.5px',
            }}
          >
            Get Ledger
          </Button>

          {/* Export to Excel Button */}
          <Button
            variant="contained"
            onClick={exportToExcel}
            disabled={ledgerEntries.length === 0}
            startIcon={<FileDownload />}
            style={{
              background: ledgerEntries.length > 0
                ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                : '#ccc',
              color: 'white',
              padding: '6px 20px',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: '700',
              boxShadow: ledgerEntries.length > 0
                ? '0 2px 8px rgba(16, 185, 129, 0.4)'
                : 'none',
              border: 'none',
              height: '35px',
              textTransform: 'none',
              letterSpacing: '0.5px',
            }}
          >
            Export to Excel
          </Button>
        </div>

        {/* Ledger Title */}
        {selectedMasterName && ledgerEntries.length > 0 && (
          <div style={{
            textAlign: 'center',
            marginBottom: '10px',
            padding: '6px',
            background: '#7b3f8f',
            borderRadius: '6px',
            color: 'white'
          }}>
            <Typography variant="h6" style={{ fontSize: '13px', fontWeight: '600' }}>
              Ledger - {selectedMasterName}
            </Typography>
          </div>
        )}

        {/* Ledger Table */}
        {ledgerEntries.length > 0 && (
          <div style={{ marginBottom: '10px' }}>
            <TableContainer component={Paper} style={{
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
              borderRadius: '10px',
              overflow: 'hidden'
            }}>
              <Table sx={{ minWidth: 650 }} aria-label="ledger table">
                <TableHead>
                  <TableRow style={{
                    background: '#7b3f8f',
                    height: '32px'
                  }}>
                    <TableCell style={{
                      color: 'white',
                      fontWeight: '600',
                      fontSize: '11px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px',
                      padding: '6px 12px',
                      textAlign: 'center',
                      width: '80px'
                    }}>
                      Sl No
                    </TableCell>
                    <TableCell style={{
                      color: 'white',
                      fontWeight: '600',
                      fontSize: '11px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px',
                      padding: '6px 12px',
                      textAlign: 'center',
                      width: '120px'
                    }}>
                      Date
                    </TableCell>
                    <TableCell style={{
                      color: 'white',
                      fontWeight: '600',
                      fontSize: '11px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px',
                      padding: '6px 12px',
                      textAlign: 'left'
                    }}>
                      description1
                    </TableCell>
                    <TableCell style={{
                      color: 'white',
                      fontWeight: '600',
                      fontSize: '11px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px',
                      padding: '6px 12px',
                      textAlign: 'right',
                      width: '150px'
                    }}>
                      debit
                    </TableCell>
                    <TableCell style={{
                      color: 'white',
                      fontWeight: '600',
                      fontSize: '11px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px',
                      padding: '6px 12px',
                      textAlign: 'right',
                      width: '150px'
                    }}>
                      credit
                    </TableCell>
                    <TableCell style={{
                      color: 'white',
                      fontWeight: '600',
                      fontSize: '11px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px',
                      padding: '6px 12px',
                      textAlign: 'right',
                      width: '150px'
                    }}>
                      Balance
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(() => {
                    let runningBalance = 0;

                    return ledgerEntries.map((ledger, index) => {
                      const isTotal = ledger.description1 === 'Total';
                      const isClosing = ledger.description1 === 'Closing Balance';
                      const isOpening = ledger.description1 === 'Opening Balance';
                      const isSpecialRow = isTotal || isClosing;

                      // Calculate running balance
                      if (isOpening) {
                        runningBalance = parseFloat(ledger.debit) || 0;
                      } else if (!isTotal && !isClosing) {
                        const debit = parseFloat(ledger.debit) || 0;
                        const credit = parseFloat(ledger.credit) || 0;
                        runningBalance = runningBalance + debit - credit;
                      } else if (isClosing) {
                        runningBalance = parseFloat(ledger.debit) || runningBalance;
                      }

                      const displayBalance = isTotal ? '' : runningBalance;
                      const isClickable = ledger.tbl1 && ledger.id && !['az', 'zy', 'zz'].includes((ledger.tbl1 || '').toLowerCase());

                      return (
                        <TableRow
                          key={index}
                          style={{
                            background: isSpecialRow
                              ? '#f0f0f0'
                              : index % 2 === 0 ? '#ffffff' : '#f8f9fa',
                            borderBottom: '1px solid #e0e0e0',
                            fontWeight: isSpecialRow ? '700' : 'normal',
                            transition: 'background-color 0.2s ease'
                          }}
                          sx={{
                            '&:hover': isClickable ? {
                              backgroundColor: '#e3f2fd !important',
                            } : {}
                          }}
                        >
                          <TableCell style={{
                            fontSize: '12px',
                            padding: '10px 12px',
                            color: '#2d3748',
                            textAlign: 'center',
                            fontWeight: isSpecialRow ? '700' : 'normal'
                          }}>
                            {!isSpecialRow ? index + 1 : ''}
                          </TableCell>
                          <TableCell style={{
                            fontSize: '12px',
                            padding: '10px 12px',
                            color: '#2d3748',
                            textAlign: 'center',
                            fontWeight: isSpecialRow ? '700' : 'normal'
                          }}>
                            {formatDate(ledger.date1)}
                          </TableCell>
                          <TableCell
                            onClick={() => handleDescriptionClick(ledger)}
                            style={{
                              fontSize: '12px',
                              padding: '10px 12px',
                              color: isClickable ? '#667eea' : '#2d3748',
                              fontWeight: isSpecialRow ? '700' : 'normal',
                              cursor: isClickable ? 'pointer' : 'default',
                              textDecoration: isClickable ? 'underline' : 'none'
                            }}
                          >
                            {ledger.description1 || '-'}
                          </TableCell>
                          <TableCell style={{
                            fontSize: '12px',
                            padding: '10px 12px',
                            color: ledger.debit && parseFloat(ledger.debit) > 0 ? '#d32f2f' : '#2d3748',
                            textAlign: 'right',
                            fontWeight: isSpecialRow ? '700' : 'normal'
                          }}>
                            {ledger.debit && parseFloat(ledger.debit) !== 0 ? (isClosing ? formatCurrency(ledger.debit) : formatCurrencyNotAED(ledger.debit)) : '-'}
                          </TableCell>
                          <TableCell style={{
                            fontSize: '12px',
                            padding: '10px 12px',
                            color: ledger.credit && parseFloat(ledger.credit) > 0 ? '#2e7d32' : '#2d3748',
                            textAlign: 'right',
                            fontWeight: isSpecialRow ? '700' : 'normal'
                          }}>
                            {ledger.credit && parseFloat(ledger.credit) !== 0 ? (isClosing ? formatCurrency(ledger.credit) : formatCurrencyNotAED(ledger.credit)) : '-'}
                          </TableCell>
                          <TableCell style={{
                            fontSize: '12px',
                            padding: '10px 12px',
                            color: isClosing
                              ? (displayBalance < 0 ? '#2e7d32' : '#d32f2f')
                              : '#2d3748',
                            textAlign: 'right',
                            fontWeight: isSpecialRow ? '700' : 'normal'
                          }}>
                            {isTotal ? '-' : (
                              <>
                                {isClosing && 'AED '}
                                {formatCurrency(Math.abs(displayBalance), false)}
                                {isClosing && (
                                  <span style={{ fontSize: '10px', marginLeft: '4px' }}>
                                    {displayBalance < 0 ? '(Cr)' : '(Dr)'}
                                  </span>
                                )}
                              </>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    });
                  })()}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        )}

        {/* No Data Message */}
        {!loading && selectedMaster && ledgerEntries.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            color: '#666'
          }}>
            <Typography variant="body1">
              No ledger entries found for the selected period.
            </Typography>
          </div>
        )}

        {/* Initial Message */}
        {!selectedMaster && (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            color: '#666'
          }}>
            <Typography variant="body1">
              Please select a master and date range to view the ledger report.
            </Typography>
          </div>
        )}
      </div>

      <LoadingOverlay open={loading} message={loadingMessage} />

      {/* Sale Modal */}
      {openSaleModal && (
        <SaleModal
          open={openSaleModal}
          onClose={handleCloseSaleModal}
          modalAction="view"
          formData={saleFormData}
          setFormData={setSaleFormData}
          saleDetails={saleDetails}
          setSaleDetails={setSaleDetails}
          relatedItems={relatedItems}
          setRelatedItems={setRelatedItems}
          onSave={handleSaveSale}
          onVerify={() => { }}
          customers={customers}
          items={items}
          banks={banks}
          loading={modalLoading}
          freightPackingGST={freightPackingGST}
          companyId={companyId}
          branchId={branchId}
          token={token}
        />
      )}

      {/* Purchase Modal */}
      {openPurchaseModal && (
        <PurchaseModal
          open={openPurchaseModal}
          onClose={handleClosePurchaseModal}
          modalAction="view"
          formData={purchaseFormData}
          setFormData={setPurchaseFormData}
          purchaseDetails={purchaseDetails}
          setPurchaseDetails={setPurchaseDetails}
          onSave={() => { }}
          onVerify={() => { }}
          onToGoodReceipt={() => { }}
          suppliers={suppliers}
          items={items}
          loading={modalLoading}
          freightPackingGST={freightPackingGST}
          companyId={companyId}
          token={token}
          setLoading={setModalLoading}
          setLoadingMessage={() => { }}
        />
      )}

      {/* Sale Return Modal */}
      {openSaleReturnModal && saleReturnData && (
        <SaleReturnModal
          open={openSaleReturnModal}
          onClose={handleCloseSaleReturnModal}
          modalAction="view"
          selectedSaleReturn={saleReturnData}
          saleReturnDetails={saleReturnDetails}
          companyId={companyId}
          branchId={branchId}
          token={token}
          customers={customers}
          setLoading={setModalLoading}
          setLoadingMessage={() => { }}
          setError={() => { }}
          setSuccess={() => { }}
          fetchSaleReturns={() => { }}
          setCreateModalOpen={setOpenSaleReturnModal}
          userId={username}
        />
      )}

      {/* Purchase Return Modal */}
      {openPurchaseReturnModal && purchaseReturnData && (
        <PurchaseReturnModal
          open={openPurchaseReturnModal}
          onClose={handleClosePurchaseReturnModal}
          onSave={() => { }}
          companyId={companyId}
          branchId={branchId}
          token={token}
          suppliers={suppliers}
          setLoading={setModalLoading}
          setLoadingMessage={() => { }}
          setError={() => { }}
          setSuccess={() => { }}
          fetchPurchaseReturns={() => { }}
          setCreateModalOpen={setOpenPurchaseReturnModal}
          userId={username}
        />
      )}

      {/* Journal Entry Modal */}
      {openJournalModal && journalData && (
        <JournalEntryModal
          open={openJournalModal}
          onClose={handleCloseJournalModal}
          modalAction="view"
          formData={journalData}
          setFormData={setJournalData}
          journalEntryDetails={journalDetails}
          setJournalEntryDetails={setJournalDetails}
          onSave={() => { }}
          masters={masters}
          gstRates={gstRates}
          loading={modalLoading}
          companyId={companyId}
          token={token}
          fetchMaxBillNo={() => { }}
          branchId={branchId}
          fromDate={fromDate}
          toDate={toDate}
        />
      )}

      {/* Payment/Receipt Modal */}
      {openPaymentModal && paymentData && (
        <PaymentReceiptsModal
          open={openPaymentModal}
          onClose={handleClosePaymentModal}
          modalAction="view"
          formData={paymentData}
          setFormData={setPaymentData}
          paymentReceiptDetails={paymentDetails}
          setPaymentReceiptDetails={setPaymentDetails}
          onSave={() => { }}
          onPrint={() => { }}
          masters={masters}
          banks={banks}
          purchases={purchases}
          sales={sales}
          paymentMethods={paymentMethods}
          vatRates={vatRates}
          loading={modalLoading}
          companyId={companyId}
          token={token}
          fetchPurchases={() => { }}
          fetchSales={() => { }}
          fetchBillsForMaster={() => { }}
          fetchMaxBillNo={() => { }}
          branchId={branchId}
          fromDate={fromDate}
          toDate={toDate}
        />
      )}
    </div>
  );
};

export default LedgerReport;
